import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Calendar, ArrowRight, Tag, Rss, Search, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SEO, getBreadcrumbSchema } from '../components/SEO';

const API = '/api';

// Placeholder image for insights without featured image
const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&q=80';

const InsightCard = ({ insight, language }) => {
  const title = language === 'ar' ? insight.title_ar : insight.title_en;
  const excerpt = language === 'ar' ? insight.excerpt_ar : insight.excerpt_en;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', options);
  };

  return (
    <Card className="group overflow-hidden hover:shadow-xl transition-all duration-300 border-0 bg-white">
      <div className="aspect-[16/10] overflow-hidden relative">
        <img 
          src={insight.featured_image || PLACEHOLDER_IMAGE}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        {insight.category && (
          <Badge className="absolute top-4 left-4 bg-[#33AADD] hover:bg-[#2890BD] text-white">
            {insight.category}
          </Badge>
        )}
      </div>
      <CardContent className="p-6">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
          <Calendar className="h-4 w-4" />
          <span>{formatDate(insight.publish_date)}</span>
        </div>
        <h3 className="text-xl font-bold text-[#111827] mb-3 line-clamp-2 group-hover:text-[#33AADD] transition-colors">
          {title}
        </h3>
        <p className="text-gray-600 mb-4 line-clamp-3">
          {excerpt}
        </p>
        <Link 
          to={`/insights/${insight.slug}`}
          className="inline-flex items-center text-[#33AADD] font-semibold hover:text-[#2890BD] transition-colors"
        >
          {language === 'ar' ? 'اقرأ المزيد' : 'Read More'}
          <ArrowRight className={`h-4 w-4 ${language === 'ar' ? 'mr-2 rotate-180' : 'ml-2'}`} />
        </Link>
      </CardContent>
    </Card>
  );
};

export default function InsightsListPage() {
  const { t, language, direction } = useLanguage();
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState({ predefined: [], used: [] });
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchInsights();
    fetchCategories();
  }, [page, selectedCategory]);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const categoryParam = selectedCategory !== 'all' ? `&category=${encodeURIComponent(selectedCategory)}` : '';
      const response = await fetch(`${API}/insights?page=${page}&limit=12${categoryParam}`);
      if (response.ok) {
        const data = await response.json();
        setInsights(data.insights);
        setTotalPages(data.total_pages);
        setTotal(data.total);
      }
    } catch (error) {
      console.error('Error fetching insights:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API}/insights/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    setPage(1);
  };

  // SEO Content
  const pageTitle = language === 'ar' 
    ? 'رؤى ويدو - مقالات الموارد البشرية والتوظيف والتدريب' 
    : 'WeDo Insights - HR, Recruitment & Training Articles';
  
  const pageDescription = language === 'ar'
    ? 'اكتشف رؤى ومقالات متخصصة في مجال الموارد البشرية والتوظيف والتدريب من ويدو لخدمات الموارد البشرية'
    : 'Discover expert insights and articles on HR, recruitment, and training from WeDo HR Services';

  const breadcrumbs = [
    { name: language === 'ar' ? 'الرئيسية' : 'Home', url: 'https://www.wedohr.net' },
    { name: language === 'ar' ? 'رؤى ويدو' : 'Insights', url: 'https://www.wedohr.net/insights' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white" dir={direction}>
      <SEO 
        title={pageTitle}
        description={pageDescription}
        canonical="https://www.wedohr.net/insights"
        additionalSchemas={[getBreadcrumbSchema(breadcrumbs)]}
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#111827] via-[#1f2937] to-[#111827] py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#33AADD] rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#33AADD] rounded-full filter blur-3xl"></div>
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {language === 'ar' ? 'رؤى ويدو' : 'WeDo Insights'}
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              {language === 'ar' 
                ? 'مقالات ورؤى متخصصة في مجال الموارد البشرية والتوظيف والتدريب'
                : 'Expert articles and insights on HR, recruitment, and corporate training'}
            </p>
            <a 
              href="/api/insights/rss.xml" 
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#33AADD] hover:text-white transition-colors"
            >
              <Rss className="h-5 w-5" />
              {language === 'ar' ? 'اشترك في RSS' : 'Subscribe via RSS'}
            </a>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 border-b bg-white sticky top-16 md:top-20 z-30">
        <div className="container-custom">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="flex items-center gap-2 text-gray-600">
              <Filter className="h-5 w-5" />
              <span className="font-medium">
                {language === 'ar' ? 'تصفية حسب الفئة:' : 'Filter by Category:'}
              </span>
            </div>
            <Select value={selectedCategory} onValueChange={handleCategoryChange}>
              <SelectTrigger className="w-full sm:w-[250px]" data-testid="category-filter">
                <SelectValue placeholder={language === 'ar' ? 'جميع الفئات' : 'All Categories'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {language === 'ar' ? 'جميع الفئات' : 'All Categories'}
                </SelectItem>
                {categories.used.map((cat) => (
                  <SelectItem key={cat.name} value={cat.name}>
                    {cat.name} ({cat.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16">
        <div className="container-custom">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-xl overflow-hidden animate-pulse">
                  <div className="aspect-[16/10] bg-gray-200"></div>
                  <div className="p-6 space-y-4">
                    <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                    <div className="h-6 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : insights.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-gray-400 mb-4">
                <Tag className="h-16 w-16 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                {language === 'ar' ? 'لا توجد مقالات' : 'No Articles Yet'}
              </h3>
              <p className="text-gray-500">
                {language === 'ar' 
                  ? 'سيتم نشر مقالات جديدة قريباً'
                  : 'New articles will be published soon'}
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-8">
                <p className="text-gray-600">
                  {language === 'ar' 
                    ? `عرض ${insights.length} من ${total} مقال`
                    : `Showing ${insights.length} of ${total} articles`}
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {insights.map((insight) => (
                  <InsightCard 
                    key={insight.insight_id} 
                    insight={insight} 
                    language={language}
                  />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-12">
                  <Button
                    variant="outline"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    {language === 'ar' ? 'السابق' : 'Previous'}
                  </Button>
                  <div className="flex items-center gap-2 px-4">
                    <span className="text-gray-600">
                      {language === 'ar' 
                        ? `صفحة ${page} من ${totalPages}`
                        : `Page ${page} of ${totalPages}`}
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    {language === 'ar' ? 'التالي' : 'Next'}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#33AADD] to-[#2890BD]">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {language === 'ar' 
              ? 'هل لديك أسئلة حول خدماتنا؟'
              : 'Have questions about our services?'}
          </h2>
          <p className="text-white/90 mb-8 max-w-2xl mx-auto">
            {language === 'ar'
              ? 'تواصل معنا لمناقشة احتياجات الموارد البشرية أو التوظيف أو التدريب الخاصة بك'
              : 'Get in touch to discuss your HR, recruitment, or training needs'}
          </p>
          <Link to="/contact">
            <Button size="lg" variant="secondary" className="bg-white text-[#33AADD] hover:bg-gray-100">
              {language === 'ar' ? 'تواصل معنا' : 'Contact Us'}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
